import { CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import { Dropdown, type MenuProps } from "antd";
import {
    FC,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { OrderType, ProductOrderItemDetails } from "tm-shared/types";

import { IgnoreRemainingConfirm } from "@/components/Modals/IgnoreRemainingConfirm";
import { useAppDispatch } from "@/hooks/redux";
import { addIgnoreRemainingOrder } from "@/store/slices/offline/offline.slice";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";
import { handleFocus } from "@/utils/dom/handleFocus";

import { ORDER_TYPES_WITH_DISABLED_CHANGES } from "../..";
import {
    StyledODIgnoreStockButton,
    StyledODIgnoreStockWrapper,
    StyledODNumberInput,
    StyledODProductBarcode,
    StyledODTableRow,
    StyledODTableRowBottom,
    StyledODTableRowController,
    StyledODTableRowCountBlock,
    StyledODTableRowMoney,
    StyledODTableRowName,
    StyledODTableRowSecondaryLabel,
    StyledODTableRowSecondaryText,
    StyledODTableRowTop,
    StyledODTableRowTopLeft,
    StyledODTableRowTopRight,
} from "./Styled";

interface IProductRow {
    item: ProductOrderItemDetails;
    isClosed: boolean;
    currentInventoryList?: Record<string, number>;
    validationErrors: Record<string, boolean>;
    orderId: string;
    barcode: string;
    onCountChange: (
        item: ProductOrderItemDetails,
        newCount: number | null,
    ) => void;
    onInputBlur: (item: ProductOrderItemDetails) => void;
    onDelete: (item: string) => void;
    ignoreRemainingsOrders: Record<string, string[]>;
    orderType?: OrderType;
    loading: boolean;
}

export const ProductRow: FC<IProductRow> = ({
    item,
    isClosed,
    currentInventoryList,
    validationErrors,
    orderId,
    barcode,
    onCountChange,
    onInputBlur,
    onDelete,
    ignoreRemainingsOrders,
    orderType,
    loading,
}) => {
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [showIgnoreWrapper, setShowIgnoreWrapper] = useState(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const tapCountRef = useRef(0);
    const tapTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const isError = validationErrors[item.barcode];
    const totalPrice = useMemo(
        () => (item.price * item.count).toFixed(2),
        [item.price, item.count],
    );
    const inventoryCount = currentInventoryList?.[item.barcode] ?? 0;
    const isIgnoreRemaining =
        ignoreRemainingsOrders[orderId]?.includes(barcode);
    const disabledEditing =
        isClosed ||
        ORDER_TYPES_WITH_DISABLED_CHANGES.includes(orderType) ||
        loading;

    const dropdownMenu: MenuProps = useMemo(
        () => ({
            items: [
                {
                    key: "delete",
                    label: (
                        <div className="flex items-center gap-1">
                            <DeleteOutlined /> Видалити
                        </div>
                    ),
                },
            ],
            onClick: () => onDelete(item.barcode),
        }),
        [item.barcode, onDelete],
    );

    const confirmIgnoreRemaining = useCallback(() => {
        dispatch(addIgnoreRemainingOrder({ orderId, barcode }));
        setIsModalOpened(false);
    }, [dispatch, orderId, barcode]);

    const cancelIgnoreRemaining = useCallback(() => {
        setIsModalOpened(false);
    }, []);

    const handleIgnoreRemaining = useCallback(() => {
        if (orderId in ignoreRemainingsOrders) {
            dispatch(addIgnoreRemainingOrder({ orderId, barcode }));
        } else {
            setIsModalOpened(true);
        }
    }, [dispatch, ignoreRemainingsOrders, orderId, barcode]);

    const handleTap = useCallback(() => {
        if (isIgnoreRemaining || disabledEditing) return;

        tapCountRef.current += 1;

        if (tapTimeoutRef.current) clearTimeout(tapTimeoutRef.current);

        tapTimeoutRef.current = setTimeout(() => {
            tapCountRef.current = 0;
        }, 300);

        if (tapCountRef.current === 2) {
            tapCountRef.current = 0;
            setShowIgnoreWrapper(true);
        }
    }, [disabledEditing, isIgnoreRemaining]);

    const handleCopyProductInfo = (e: SyntheticEvent) => {
        e.stopPropagation();
        copyObjectToClipboard({
            barcode: item.barcode,
            name: item.name,
            orderId,
            price: item.price,
            itemsLeft: inventoryCount,
        });
    };

    const numberInputProps = useMemo(
        () => ({
            min: 0,
            onFocus: handleFocus,
            value: item.count,
            inputMode: "numeric" as const,
            disabled: disabledEditing,
            parser: (val: string = "") => {
                const parsed = parseInt(val.replace(/\D/g, ""), 10);
                return isNaN(parsed) ? 0 : parsed;
            },
            formatter: (val: string) => val.replace(/\D/g, ""),
            isError,
            onChange: (val: string | number) => {
                const number =
                    typeof val === "number" ? val : parseInt(val, 10);
                onCountChange(item, isNaN(number) ? 0 : number);
            },
            onBlur: () => onInputBlur(item),
            onInput: () => {},
        }),
        [item, disabledEditing, isError, onCountChange, onInputBlur],
    );

    useEffect(() => {
        return () => {
            if (tapTimeoutRef.current) {
                clearTimeout(tapTimeoutRef.current);
            }
        };
    }, []);

    return (
        <StyledODTableRow>
            <IgnoreRemainingConfirm
                modalKey={item.barcode}
                isModalOpened={isModalOpened}
                confirmRemaining={confirmIgnoreRemaining}
                cancelRemaining={cancelIgnoreRemaining}
            />

            <StyledODTableRowTop>
                <StyledODTableRowTopLeft>
                    <StyledODTableRowName
                        onClick={() =>
                            navigate(`/work-plan/product-card/${barcode}`)
                        }
                    >
                        {item.name}
                    </StyledODTableRowName>

                    <StyledODProductBarcode
                        style={{ marginTop: 4, width: "fit-content" }}
                        onClick={handleCopyProductInfo}
                    >
                        <StyledODTableRowSecondaryLabel>
                            Код:
                        </StyledODTableRowSecondaryLabel>{" "}
                        {item.barcode} <CopyOutlined />
                    </StyledODProductBarcode>

                    {orderType !== "r" && (
                        <StyledODTableRowSecondaryText>
                            <StyledODTableRowSecondaryLabel>
                                Залишок:
                            </StyledODTableRowSecondaryLabel>{" "}
                            {inventoryCount}
                        </StyledODTableRowSecondaryText>
                    )}
                </StyledODTableRowTopLeft>

                <StyledODTableRowTopRight>
                    {!disabledEditing && (
                        <Dropdown menu={dropdownMenu}>
                            <StyledODTableRowController />
                        </Dropdown>
                    )}
                </StyledODTableRowTopRight>
            </StyledODTableRowTop>

            {orderType !== "r" ? (
                <StyledODTableRowBottom>
                    <StyledODTableRowMoney>{item.price}₴</StyledODTableRowMoney>
                    <StyledODTableRowCountBlock onTouchStart={handleTap}>
                        <StyledODNumberInput {...numberInputProps} />
                    </StyledODTableRowCountBlock>
                    <StyledODTableRowMoney>{totalPrice}₴</StyledODTableRowMoney>
                </StyledODTableRowBottom>
            ) : (
                <StyledODTableRowSecondaryText>
                    Кількість: {item.count}
                </StyledODTableRowSecondaryText>
            )}

            {showIgnoreWrapper && !isIgnoreRemaining && (
                <StyledODIgnoreStockWrapper>
                    <StyledODIgnoreStockButton
                        type="primary"
                        onClick={handleIgnoreRemaining}
                    >
                        Ігнорувати залишки
                    </StyledODIgnoreStockButton>
                </StyledODIgnoreStockWrapper>
            )}
        </StyledODTableRow>
    );
};
